import './ShipperForm.scss';

import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Form, Input, Tag } from 'antd';
import { FormProps } from 'antd/lib';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { COUNTRY, useCountryContext } from '@/contexts/CountryContext';

import { useShipperLocationForm } from './useShipperLocationForm';

interface ShipperFormProps {
  formProps: FormProps;
  edit?: boolean;
  /** Valores iniciales transformados (IDs planos de país/provincia/localidad) */
  initialValuesModified?: any;
}

const ShipperForm = (props: ShipperFormProps) => {
  const { t } = useTranslation();
  const { formProps, edit, initialValuesModified } = props;
  const { initialValues } = formProps;
  const { isBrasilSelected: isBrasilContext } = useCountryContext();
  const {
    pais,
    provincia,
    isBrasilSelected,
    paisesSelectProps,
    provinciasSelectProps,
    localidadesSelectProps,
    localidadesOptions,
    cepLookupState,
    handleLocalidadChange,
    enableBrazilManualLocation,
  } = useShipperLocationForm({ formProps, initialValuesModified });
  const postalCodeLabel = isBrasilSelected ? 'CEP' : t('Código postal');
  const canEditLocation = !isBrasilSelected || cepLookupState.canEditLocationManually;
  const canEditAddress = !isBrasilSelected || cepLookupState.canEditAddressManually;

  return (
    <Form
      {...formProps}
      // Combina initialValues de Refine (edición) con los valores transformados (pais default)
      // para garantizar que el select de país se inicialice correctamente desde el mount.
      initialValues={{ ...formProps.initialValues, ...initialValuesModified }}
      onFinish={values => {
        // Asegura que el payload siempre incluya el país correcto para Brasil,
        // ya que el select está deshabilitado y podría no incluirse.
        if (isBrasilContext) {
          values.pais = COUNTRY.BRASIL;
        }

        formProps.onFinish?.(values);
      }}
      layout="vertical"
      className="ShipperForm">
      {edit && initialValues?.id && (
        <div className="ShipperFormShowDataId">
          Id del operador: <span>{initialValues.id}</span>
        </div>
      )}
      <Divider className="ShipperFormDivider" plain>
        {t('Datos contacto')}
      </Divider>
      <div className="ShipperFormDataContainer">
        <Form.Item
          label={t('Nombre')}
          name={'name'}
          rules={[
            {
              required: true,
              message: t('El nombre es requerido'),
            },
          ]}>
          <Input />
        </Form.Item>
        <Form.Item label={t('Teléfono')} name={'phone'}>
          <Input />
        </Form.Item>
        <Form.Item label={`${t('Teléfono')} 2`} name={'phone2'}>
          <Input />
        </Form.Item>
        <Form.Item
          label={t('Email')}
          name={'email'}
          rules={[{ type: 'email', message: t('Email inválido') }]}>
          <Input />
        </Form.Item>
        <Form.Item
          label={t('Link de tracking')}
          name={'tracking_link'}
          rules={[{ type: 'url', message: t('URL inválido') }]}>
          <Input />
        </Form.Item>
      </div>
      <Divider className="ShipperFormDivider" plain>
        {t('Dirección')}
      </Divider>
      {/* Sección de ubicación con selects encadenados:
          - País: disabled en Brasil (fijo), seleccionable para otros países (default: Argentina)
          - Brasil: CEP → autocompleta UF/Municipio/Dirección
          - No-Brasil: País → Provincia → Localidad → Código Postal (manual) */}

      <div className="ShipperFormRow">
        {/* País: deshabilitado si el contexto es Brasil, editable en otros casos */}
        <Form.Item label={t('País')} name={'pais'}>
          <AntdSelect {...paisesSelectProps} allowClear showSearch disabled={isBrasilSelected} />
        </Form.Item>
        {isBrasilSelected ? (
          <Form.Item
            label={postalCodeLabel}
            name={'postal_code'}
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', { field: postalCodeLabel }),
              },
            ]}>
            <Input suffix={cepLookupState.loading && <LoadingOutlined />} />
          </Form.Item>
        ) : (
          <Form.Item label={t('Provincia')} name={'provincia'}>
            <AntdSelect {...provinciasSelectProps} disabled={!pais} allowClear showSearch />
          </Form.Item>
        )}
      </div>

      {/* Alerta visible cuando el CEP no se encuentra: permite cargar la ubicación manualmente */}
      {isBrasilSelected && cepLookupState.error && (
        <Alert
          style={{ marginBottom: 8 }}
          type="warning"
          showIcon
          message={t('No se pudo encontrar una dirección para el CEP ingresado')}
          action={
            !canEditLocation ? (
              <Button size="small" onClick={enableBrazilManualLocation}>
                {t('Cargar manualmente')}
              </Button>
            ) : (
              <Tag color="warning">{t('Carga manual')}</Tag>
            )
          }
        />
      )}

      <div className="ShipperFormRow">
        {isBrasilSelected && (
          <Form.Item label={'UF'} name={'provincia'}>
            <AntdSelect {...provinciasSelectProps} disabled={!canEditLocation} allowClear />
          </Form.Item>
        )}
        <Form.Item label={isBrasilSelected ? t('Municipio') : t('Localidad')} name={'localidad'}>
          <AntdSelect
            {...localidadesSelectProps}
            options={localidadesOptions}
            onChange={handleLocalidadChange}
            disabled={!pais || !provincia || !canEditLocation}
            allowClear
            showSearch
          />
        </Form.Item>
        {!isBrasilSelected && (
          <Form.Item label={postalCodeLabel} name={'postal_code'}>
            <Input />
          </Form.Item>
        )}
      </div>

      <div className="ShipperFormRow">
        <Form.Item label={t('Dirección')} name={'address'}>
          <Input disabled={!canEditAddress} />
        </Form.Item>
        {isBrasilSelected && (
          <>
            <Form.Item label={t('Número')} name={'address_number'}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Complemento')} name={'address_complement'}>
              <Input />
            </Form.Item>
          </>
        )}
      </div>
    </Form>
  );
};

export default ShipperForm;
